<template>
  <section id="compatibility" class="compatibility-section">
    <div class="comp-container">
      <div class="section-badge">
        <span>{{ t.compatibility.badge }}</span>
      </div>

      <h2 class="section-title">{{ t.compatibility.title }}</h2>
      <span class="section-subtitle-gradient">{{ t.compatibility.tagline }}</span>

      <p class="section-subtitle">
        {{ t.compatibility.description }}
      </p>

      <!-- Interactive Model Checker -->
      <div class="checker-box">
        <div class="search-bar-wrapper">
          <span class="search-icon">🔍</span>
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t.compatibility.searchPlaceholder"
            class="device-search-input"
          />
          <button v-if="searchQuery" @click="searchQuery = ''" class="clear-btn">✕</button>
        </div>

        <div class="filter-pills">
          <button
            @click="filterType = 'all'"
            class="filter-pill"
            :class="{ active: filterType === 'all' }"
          >
            {{ t.compatibility.filterAll }} ({{ devicesList.length }})
          </button>
          <button
            @click="filterType = 'ai'"
            class="filter-pill highlight-pill"
            :class="{ active: filterType === 'ai' }"
          >
            {{ t.compatibility.filterAi }} ({{ aiDevicesCount }})
          </button>
        </div>

        <!-- Devices Grid -->
        <div class="devices-grid">
          <div
            v-for="(device, dIdx) in filteredDevices"
            :key="dIdx"
            class="device-card"
            :class="{ 'has-ai': device.hasAI }"
          >
            <div class="device-card-header">
              <span class="device-name">{{ device.name }}</span>
              <span v-if="device.isNew" class="new-tag">{{ t.compatibility.flagshipTag }}</span>
            </div>

            <div class="chip-info">
              <span class="chip-label">{{ t.compatibility.chipLabel }}</span>
              <span class="chip-name">{{ device.chip }}</span>
            </div>

            <div class="support-badges">
              <div class="badge-item supported">
                <span class="badge-dot">●</span>
                <span>{{ t.compatibility.supportedBadge }}</span>
              </div>
              <div class="badge-item" :class="device.hasAI ? 'ai-supported' : 'ai-not-supported'">
                <span class="badge-dot">●</span>
                <span>{{ device.hasAI ? t.compatibility.aiSupported : t.compatibility.standardFeatures }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="filteredDevices.length === 0" class="no-results">
          <p>{{ t.compatibility.noResults.replace('{query}', searchQuery) }}</p>
        </div>
      </div>

      <!-- How to Update Card -->
      <div class="update-guide-card">
        <div class="guide-content">
          <span class="card-eyebrow">{{ t.compatibility.upgradeEyebrow }}</span>
          <h3 class="guide-title">{{ t.compatibility.upgradeTitle }}</h3>
          <ol class="update-steps">
            <li v-html="t.compatibility.step1"></li>
            <li v-html="t.compatibility.step2"></li>
            <li v-html="t.compatibility.step3"></li>
          </ol>
        </div>
        <div class="guide-action">
          <router-link to="/form" class="btn btn-primary">
            {{ t.compatibility.preOrderAction }}
          </router-link>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { useI18n } from '@/i18n';

export default {
  name: 'CompatibilitySection',
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      searchQuery: '',
      filterType: 'all',
      devicesList: [
        { name: 'iPhone 18 Pro Max', chip: 'A19 Pro (Next Gen)', hasAI: true, isNew: true },
        { name: 'iPhone 18 Pro', chip: 'A19 Pro (Next Gen)', hasAI: true, isNew: true },
        { name: 'iPhone 18', chip: 'A19 (Next Gen)', hasAI: true, isNew: true },
        { name: 'iPhone 16 Pro Max', chip: 'A18 Pro', hasAI: true, isNew: true },
        { name: 'iPhone 16 Pro', chip: 'A18 Pro', hasAI: true, isNew: true },
        { name: 'iPhone 16 Plus', chip: 'A18', hasAI: true, isNew: true },
        { name: 'iPhone 16', chip: 'A18', hasAI: true, isNew: true },
        { name: 'iPhone 15 Pro Max', chip: 'A17 Pro', hasAI: true, isNew: false },
        { name: 'iPhone 15 Pro', chip: 'A17 Pro', hasAI: true, isNew: false },
        { name: 'iPhone 15 Plus', chip: 'A16 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone 15', chip: 'A16 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone 14 Pro Max', chip: 'A16 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone 14 Pro', chip: 'A16 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone 14 Plus', chip: 'A15 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone 14', chip: 'A15 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone 13 Pro Max', chip: 'A15 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone 13 Pro', chip: 'A15 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone 13', chip: 'A15 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone 13 mini', chip: 'A15 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone 12 Pro Max', chip: 'A14 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone 12 Pro', chip: 'A14 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone 12', chip: 'A14 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone 12 mini', chip: 'A14 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone 11 Pro Max', chip: 'A13 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone 11 Pro', chip: 'A13 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone 11', chip: 'A13 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone XS & XS Max', chip: 'A12 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone XR', chip: 'A12 Bionic', hasAI: false, isNew: false },
        { name: 'iPhone SE (2nd gen or later)', chip: 'A13 Bionic / A15 Bionic', hasAI: false, isNew: false }
      ]
    };
  },
  computed: {
    aiDevicesCount() {
      return this.devicesList.filter(d => d.hasAI).length;
    },
    filteredDevices() {
      let list = this.devicesList;
      if (this.filterType === 'ai') {
        list = list.filter(d => d.hasAI);
      }
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase().trim();
        list = list.filter(d => d.name.toLowerCase().includes(q) || d.chip.toLowerCase().includes(q));
      }
      return list;
    }
  }
}
</script>

<style scoped>
.compatibility-section {
  padding: 100px 24px;
  background: #f5f5f7;
  color: #1d1d1f;
  text-align: center;
}

.comp-container {
  max-width: 1120px;
  margin: 0 auto;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.06);
  color: #1d1d1f;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 980px;
  margin-bottom: 20px;
}

.section-title {
  font-size: clamp(34px, 5.5vw, 56px);
  font-weight: 600;
  letter-spacing: -0.005em;
  line-height: 1.07143;
  color: #1d1d1f;
  margin-bottom: 4px;
}

.section-subtitle-gradient {
  font-size: clamp(30px, 5vw, 52px);
  line-height: 1.1;
  font-weight: 600;
  letter-spacing: 0.004em;
  background: linear-gradient(to right,
      #ff3b30,
      #ff9500,
      #b59722,
      #4c63d9,
      #187099,
      #007aff,
      #36349e,
      #3211b4);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  display: inline-block;
  margin-bottom: 20px;
}

.section-subtitle {
  max-width: 760px;
  margin: 0 auto 50px;
  font-size: 17px;
  line-height: 1.45;
  color: #86868b;
  font-weight: 400;
}

/* Checker Box */
.checker-box {
  background: #ffffff;
  border-radius: 28px;
  padding: 40px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.05);
  margin-bottom: 50px;
}

.search-bar-wrapper {
  position: relative;
  max-width: 600px;
  margin: 0 auto 24px;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 18px;
  font-size: 16px;
  color: #86868b;
  pointer-events: none;
}

.device-search-input {
  width: 100%;
  padding: 14px 44px 14px 48px;
  border-radius: 980px;
  border: 1px solid #d2d2d7;
  font-size: 16px;
  outline: none;
  background: #f5f5f7;
  transition: all 0.2s ease;
}

.device-search-input:focus {
  border-color: #0071e3;
  background: #ffffff;
  box-shadow: 0 0 0 4px rgba(0, 113, 227, 0.15);
}

.clear-btn {
  position: absolute;
  right: 16px;
  background: #86868b;
  color: #ffffff;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.filter-pills {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 36px;
  flex-wrap: wrap;
}

.filter-pill {
  padding: 8px 20px;
  border-radius: 980px;
  border: 1px solid #d2d2d7;
  background: #ffffff;
  color: #1d1d1f;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-pill.active {
  background: #1d1d1f;
  color: #ffffff;
  border-color: #1d1d1f;
}

.highlight-pill.active {
  background: linear-gradient(135deg, #ff3b30, #ff9500, #007aff);
  border-color: transparent;
}

/* Devices Grid */
.devices-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  max-height: 480px;
  overflow-y: auto;
  padding: 10px 4px;
  scrollbar-width: thin;
}

.device-card {
  background: #f5f5f7;
  border-radius: 18px;
  padding: 20px;
  text-align: left;
  border: 1px solid rgba(0, 0, 0, 0.04);
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.device-card:hover {
  transform: translateY(-2px);
  border-color: rgba(0, 113, 227, 0.2);
}

.device-card.has-ai {
  background: #ffffff;
  border: 1px solid rgba(0, 113, 227, 0.2);
  box-shadow: 0 4px 16px rgba(0, 113, 227, 0.06);
}

.device-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.device-name {
  font-size: 17px;
  font-weight: 600;
  color: #1d1d1f;
}

.new-tag {
  font-size: 11px;
  background: rgba(0, 113, 227, 0.1);
  color: #0071e3;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
}

.chip-info {
  font-size: 13px;
  color: #86868b;
  margin-bottom: 14px;
}

.chip-name {
  color: #515154;
  font-weight: 500;
  margin-left: 4px;
}

.support-badges {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.badge-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
}

.badge-item.supported {
  color: #34c759;
}

.badge-item.ai-supported {
  background: linear-gradient(90deg, #ff3b30, #ff9500, #007aff);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-weight: 600;
}

.badge-item.ai-not-supported {
  color: #86868b;
}

.badge-dot {
  font-size: 8px;
}

.no-results {
  padding: 40px 20px;
  color: #86868b;
  font-size: 16px;
}

/* Update Guide Card */
.update-guide-card {
  background: #ffffff;
  border-radius: 28px;
  padding: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 30px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.05);
  text-align: left;
}

.card-eyebrow {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  color: #0071e3;
  display: block;
  margin-bottom: 8px;
}

.guide-title {
  font-size: 26px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 16px;
}

.update-steps {
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #515154;
  font-size: 15px;
}

.guide-action .btn-primary {
  padding: 14px 28px;
  font-size: 16px;
  white-space: nowrap;
}

@media (max-width: 860px) {
  .update-guide-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .guide-action {
    width: 100%;
  }

  .guide-action .btn-primary {
    width: 100%;
    justify-content: center;
  }
}
</style>
